import { useState } from "react"

import { Alert, App, Button, Checkbox, Flex, Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import {
  useAgentPlatform,
  useDisconnectConfirm
} from "@/pages/DeploymentsPage/application/useAgentPlatform"
import {
  AGENTCORE_STACK_NAME,
  DEFAULT_AGENTCORE_CODE_ACCESS_INPUT,
  DEFAULT_AGENTCORE_SETTINGS,
  getAgentCoreSettingsRequest,
  getStackCodeAccess,
  isAgentCoreSettingsValid,
  isAgentCoreTemplateChanged
} from "@/pages/DeploymentsPage/application/useAgentPlatform.selector"
import {
  CloudConnectionSettingsModal,
  getCloudConnectionName
} from "@/pages/DeploymentsPage/components/molecules/CloudConnectionStatus"
import { AGENTCORE_NAME } from "./AgentCoreCloudConnection.constants"
import { AgentCoreCodeAccessFields } from "./AgentCoreCodeAccessFields"
import { AgentCoreSettingsFields } from "./AgentCoreSettingsFields"
import { AgentCoreTemplate } from "./AgentCoreTemplate"
import { useAgentCoreTemplate } from "./useAgentCoreTemplate"

import styles from "./AgentCoreCloudConnection.module.scss"

import type { AgentCoreSettings } from "@/api/useDeployments.types"
import type { AgentCoreSettingsModalProps } from "."

// An AWS account's connection: its role, and its settings to edit. Every
// setting is in the template, so a change waits for the stack to be updated
// with it.
export const AgentCoreSettingsModal = ({
  connection,
  onClose
}: AgentCoreSettingsModalProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "deployments.agentCore.cloud"
  })
  const { t: tConnection } = useTranslation("translation", {
    keyPrefix: "deployments.cloudConnection"
  })
  const { message } = App.useApp()
  const platform = useAgentPlatform("agentcore")
  const disconnect = useDisconnectConfirm("agentcore")
  const template = useAgentCoreTemplate(platform.preInstall)
  const connected = { ...DEFAULT_AGENTCORE_SETTINGS, ...connection.settings }
  const [settings, setSettings] = useState<AgentCoreSettings>(connected)
  const [confirmed, setConfirmed] = useState(false)
  // The stack updated for its code access alone, with no setting to save
  const [stackOnly, setStackOnly] = useState(false)
  const [codeAccess, setCodeAccess] = useState(
    DEFAULT_AGENTCORE_CODE_ACCESS_INPUT
  )
  const name = getCloudConnectionName(AGENTCORE_NAME, connection)
  // The template holds every setting, so any change needs the stack updated
  const changed = isAgentCoreTemplateChanged(connection.settings, settings)

  const edit = (next: AgentCoreSettings) => {
    setSettings(next)
    setConfirmed(false)
    template.reset()
  }

  const onSave = async () => {
    const saved = await platform.update(
      connection.connectionId,
      getAgentCoreSettingsRequest(settings)
    )

    if (!saved) return

    message.success(tConnection("updated", { name }))
    onClose()
  }

  return (
    <CloudConnectionSettingsModal
      name={name}
      identity={[
        { label: t("fields.roleArn.label"), value: connection.roleArn }
      ]}
      saveDisabled={
        !changed || !isAgentCoreSettingsValid(settings) || !confirmed
      }
      isSaving={platform.isUpdating}
      onSave={onSave}
      onDisconnect={() => disconnect(connection.connectionId, name)}
      onClose={onClose}
    >
      <Flex vertical gap={16}>
        <AgentCoreSettingsFields value={settings} onChange={edit} />
        {!changed && !stackOnly && (
          <div>
            <Button
              type="link"
              className={styles.inlineLink}
              onClick={() => setStackOnly(true)}
            >
              {t("codeAccess.update")}
            </Button>
          </div>
        )}
        {changed && (
          <Alert
            showIcon
            type="warning"
            title={t("templateWarning", { stack: AGENTCORE_STACK_NAME })}
          />
        )}
        {(changed || stackOnly) && (
          <>
            <AgentCoreCodeAccessFields
              isUpdate
              value={codeAccess}
              onChange={setCodeAccess}
            />
            <AgentCoreTemplate
              isUpdate
              regions={settings.regions}
              getTemplate={() => template.getTemplate(settings)}
              codeAccess={getStackCodeAccess(codeAccess)}
            />
          </>
        )}
        {changed && (
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          >
            {t("updated")}
          </Checkbox>
        )}
        {!changed && stackOnly && (
          <Text type="secondary">{t("codeAccess.nothingToSave")}</Text>
        )}
        {platform.error && (
          <Alert
            showIcon
            type="error"
            title={platform.error}
            closable={{ onClose: platform.clearError }}
          />
        )}
      </Flex>
    </CloudConnectionSettingsModal>
  )
}
